import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clapperboard, Wand2, Download, X, Loader2, Sparkles,
  Lock, Crown, ChevronDown, RefreshCw, Scan, ImageIcon,
  User, History, Trash2, Building2, Star, Play, Globe
} from 'lucide-react';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { PublishModal } from './VeritasContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASPECT_RATIOS = [
  { label: '16:9', value: '16:9', desc: 'Panorámico' },
  { label: '9:16', value: '9:16', desc: 'Vertical' },
];

const EXAMPLE_PROMPTS = [
  'Un astronauta caminando por la superficie de Marte al atardecer, cinematográfico',
  'Olas del océano rompiendo contra acantilados rocosos, cámara lenta, 4K',
  'Ciudad futurista de noche con coches voladores y luces de neón',
  'Un lobo corriendo por un bosque nevado en cámara lenta',
  'Lapso de tiempo de nubes moviéndose sobre montañas al amanecer',
];

const STYLES = [
  { label: 'Cinematográfico', value: 'cinematic, film quality' },
  { label: 'Documental', value: 'documentary style, realistic' },
  { label: 'Cámara lenta', value: 'slow motion, high frame rate' },
  { label: 'Lapso de tiempo', value: 'time lapse' },
  { label: 'Animación', value: 'animated, stylized' },
  { label: 'Sin estilo', value: '' },
];

export default function VeritasVideo({ user, onNavigate }) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('cinematic, film quality');
  const [ratio, setRatio] = useState('16:9');
  const [duration, setDuration] = useState(10);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [referenceImage, setReferenceImage] = useState(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState(null);
  const imageInputRef = useRef(null);

  const handleReferenceImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setReferenceImage(ev.target.result);
      setReferenceImagePreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };
  const [error, setError] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showStyleMenu, setShowStyleMenu] = useState(false);
  const [generationCount, setGenerationCount] = useState(() => {
    if (user?.plan === 'pro' || user?.plan === 'business') return 0;
    const today = new Date().toDateString();
    try {
      const saved = JSON.parse(localStorage.getItem('veritas_video_count') || '{}');
      return saved.date === today ? (saved.count || 0) : 0;
    } catch { return 0; }
  });
  const [showHistory, setShowHistory] = useState(false);
  const [videoHistory, setVideoHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('veritas_video_history') || '[]'); } catch { return []; }
  });

  const isPro = user?.plan === 'pro' || user?.plan === 'business';
  const FREE_LIMIT = 1; // Veo 3 es caro ($0.75/s), solo 1 gratis
  const canGenerate = isPro || generationCount < FREE_LIMIT;

  const handleGenerate = async () => {
    if (!prompt.trim() || !canGenerate) return;
    setIsGenerating(true);
    setError(null);
    setGeneratedVideo(null);

    try {
      const token = localStorage.getItem('veritas_token');
      const finalPrompt = style ? `${prompt}, ${style}` : prompt;

      const res = await fetch(`${API_URL}/api/video/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prompt: finalPrompt, aspect_ratio: ratio, duration_seconds: duration, image_base64: referenceImage || undefined }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Error al generar el vídeo');
      }

      const data = await res.json();
      const videoUrl = data.video_url;
      setGeneratedVideo(videoUrl);
      setCurrentPrompt(prompt);

      // Guardar solo metadatos en historial — el video base64 es demasiado grande para localStorage
      const entry = { id: Date.now(), prompt, style, ratio, created_at: new Date().toISOString() };
      const updated = [entry, ...videoHistory].slice(0, 20);
      setVideoHistory(updated);
      try {
        localStorage.setItem('veritas_video_history', JSON.stringify(updated));
      } catch (e) {
        // Si supera la cuota, limpiar historial viejo
        localStorage.removeItem('veritas_video_history');
        localStorage.setItem('veritas_video_history', JSON.stringify([entry]));
        setVideoHistory([entry]);
      }

      if (!isPro) {
        const newCount = generationCount + 1;
        setGenerationCount(newCount);
        localStorage.setItem('veritas_video_count', JSON.stringify({ date: new Date().toDateString(), count: newCount }));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedVideo) return;
    const a = document.createElement('a');
    a.href = generatedVideo;
    a.download = `veritas-video-${Date.now()}.mp4`;
    a.click();
  };

  const selectedStyle = STYLES.find(s => s.value === style) || STYLES[STYLES.length - 1];

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(239, 68, 68, 0.06), transparent 60%)' }} />

      {/* Header */}
      <header className="relative z-10 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          {/* Logo */}
          <button onClick={() => onNavigate('home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0">
            <Clapperboard className="w-6 h-6 sm:w-7 sm:h-7 text-red-500" />
            <h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">VERITAS <span className="text-red-500">VIDEO</span></h1>
          </button>

          {/* Product selector */}
          <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
            <button onClick={() => onNavigate('home')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all text-zinc-500 hover:text-zinc-300">
              <Scan className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">SCAN</span>
            </button>
            <button onClick={() => onNavigate('image')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all text-zinc-500 hover:text-zinc-300">
              <ImageIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">IMAGE</span>
            </button>
            <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all bg-red-600 text-white">
              <Clapperboard className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">VIDEO</span>
            </button>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            {isPro && (
              user?.plan === 'business'
                ? <div className="hidden lg:flex items-center gap-1 px-3 py-1 rounded-full font-mono text-xs bg-purple-500/10 border border-purple-500/30 text-purple-400"><Building2 className="w-3 h-3" /> Business</div>
                : <div className="hidden lg:flex items-center gap-1 px-3 py-1 rounded-full font-mono text-xs bg-yellow-500/10 border border-yellow-500/30 text-yellow-400"><Star className="w-3 h-3" /> Pro</div>
            )}
            <button onClick={() => navigate('/blog')} className="hidden sm:flex items-center gap-1 px-3 py-2 text-zinc-400 hover:text-white font-mono uppercase tracking-wider text-xs sm:text-sm transition-all">Blog</button>
            <button onClick={() => onNavigate('precios')} className="hidden sm:flex items-center gap-1 px-3 py-2 text-zinc-400 hover:text-white font-mono uppercase tracking-wider text-xs sm:text-sm transition-all">Precios</button>
            <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono uppercase tracking-wider text-xs sm:text-sm rounded-sm transition-all">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Historial</span>
              {videoHistory.length > 0 && <span className="px-1.5 py-0.5 bg-zinc-800 rounded-full text-xs">{videoHistory.length}</span>}
            </button>
            {user ? (
              <button onClick={() => onNavigate('profile')} className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 font-mono text-xs sm:text-sm rounded-sm transition-all">
                <User className="w-4 h-4" /><span className="hidden sm:inline">{user.name?.split(' ')[0]}</span>
              </button>
            ) : (
              <button onClick={() => onNavigate('auth')} className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-mono uppercase tracking-wider text-xs sm:text-sm rounded-sm transition-all">
                <span className="hidden sm:inline">Iniciar sesión</span>
                <span className="sm:hidden">Entrar</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 font-mono text-xs uppercase tracking-widest mb-4">
            <Sparkles className="w-3 h-3" /> Generador de vídeo con IA · Seedance 2.0
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight mb-3">
            VERITAS <span className="text-red-500">VIDEO</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-lg mx-auto">
            Describe una escena y Seedance 2.0 la convierte en un vídeo cinematográfico con audio nativo.
          </p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <span className="text-zinc-600 font-mono text-xs">Powered by</span>
            <span className="text-zinc-400 font-mono text-xs font-bold">Seedance 2.0 · ByteDance</span>
            <span className="px-2 py-0.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-full font-mono text-xs">5-20s · 1080p</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">

          {/* LEFT: Controls */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">

            {/* Prompt */}
            <div className="space-y-2">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Descripción de la escena</label>
              <div className="relative">
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="Describe la escena con el máximo detalle posible: cámara, luz, acción, ambiente..."
                  rows={5}
                  disabled={isGenerating}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-4 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-red-500 font-mono text-sm transition-colors resize-none"
                />
                <span className="absolute bottom-3 right-3 text-zinc-600 font-mono text-xs">{prompt.length}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_PROMPTS.slice(0, 3).map((p, i) => (
                  <button key={i} onClick={() => setPrompt(p)}
                    className="text-xs text-zinc-500 hover:text-red-400 border border-zinc-800 hover:border-red-500/40 rounded-full px-3 py-1 transition-all font-mono truncate max-w-[220px]">
                    {p.substring(0, 35)}…
                  </button>
                ))}
              </div>
            </div>

            {/* Reference image */}
            <div className="space-y-2">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Imagen de referencia <span className="text-zinc-700">(opcional)</span></label>
              <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleReferenceImage} />
              {referenceImagePreview ? (
                <div className="relative rounded-xl overflow-hidden border border-red-500/30 bg-zinc-900">
                  <img src={referenceImagePreview} alt="Referencia" className="w-full max-h-40 object-cover" />
                  <button onClick={() => { setReferenceImage(null); setReferenceImagePreview(null); }}
                    className="absolute top-2 right-2 p-1.5 bg-zinc-900/80 backdrop-blur rounded-lg text-zinc-400 hover:text-red-400 border border-zinc-700 transition-all">
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-red-600/80 rounded-full font-mono text-xs text-white">Imagen cargada</div>
                </div>
              ) : (
                <button onClick={() => imageInputRef.current?.click()}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-zinc-900 border border-dashed border-zinc-700 hover:border-red-500/50 rounded-xl text-zinc-500 hover:text-red-400 transition-all">
                  <ImageIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="font-mono text-sm">Subir imagen de referencia (image-to-video)</span>
                </button>
              )}
            </div>

            {/* Style */}
            <div className="space-y-2">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Estilo</label>
              <div className="relative">
                <button onClick={() => setShowStyleMenu(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-zinc-900 border border-zinc-700 hover:border-zinc-500 rounded-xl text-sm font-mono text-zinc-300 transition-all">
                  <span>{selectedStyle.label}</span>
                  <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showStyleMenu ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {showStyleMenu && (
                    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                      className="absolute top-full mt-1 left-0 right-0 bg-zinc-900 border border-zinc-700 rounded-xl overflow-hidden z-20 shadow-xl">
                      {STYLES.map(s => (
                        <button key={s.value} onClick={() => { setStyle(s.value); setShowStyleMenu(false); }}
                          className={`w-full text-left px-4 py-2.5 text-sm font-mono transition-colors ${style === s.value ? 'bg-red-500/20 text-red-300' : 'text-zinc-400 hover:bg-zinc-800'}`}>
                          {s.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Aspect ratio */}
            <div className="space-y-2">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Proporción</label>
              <div className="flex gap-2">
                {ASPECT_RATIOS.map(r => (
                  <button key={r.value} onClick={() => setRatio(r.value)}
                    className={`flex-1 py-3 rounded-xl border font-mono text-sm transition-all ${ratio === r.value ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}>
                    <div className="font-bold">{r.label}</div>
                    <div className="text-xs opacity-60 mt-0.5">{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Duración */}
            <div className="space-y-2">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Duración</label>
              <div className="flex gap-2">
                {[5, 10, 15, 20].map(d => (
                  <button key={d} onClick={() => setDuration(d)}
                    className={`flex-1 py-2.5 rounded-xl border font-mono text-sm transition-all ${
                      duration === d ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                    }`}>
                    {d}s
                  </button>
                ))}
              </div>
            </div>

            {/* Info card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 text-xs font-mono text-zinc-500">
                <Clapperboard className="w-3.5 h-3.5 text-red-500" />
                <span>Seedance 2.0 genera vídeos de <strong className="text-zinc-400">{duration} segundos</strong> en 1080p</span>
              </div>
              <div className="flex items-center gap-2 text-xs font-mono text-zinc-500">
                <Sparkles className="w-3.5 h-3.5 text-red-500" />
                <span>La generación puede tardar <strong className="text-zinc-400">1-3 minutos</strong></span>
              </div>
            </div>

            {/* Free limit notice */}
            {!isPro && (
              <div className={`flex items-center gap-3 rounded-xl p-3 border text-sm font-mono ${
                generationCount >= FREE_LIMIT
                  ? 'bg-red-500/10 border-red-500/30 text-red-400'
                  : 'bg-zinc-900 border-zinc-800 text-zinc-500'
              }`}>
                {generationCount >= FREE_LIMIT ? <Lock className="w-4 h-4 flex-shrink-0" /> : <Sparkles className="w-4 h-4 flex-shrink-0" />}
                {generationCount >= FREE_LIMIT
                  ? <span>Límite gratuito alcanzado. <button onClick={() => onNavigate('precios')} className="text-yellow-400 underline">Actualiza a Pro</button> para generaciones ilimitadas.</span>
                  : <span>{FREE_LIMIT - generationCount} generación gratuita restante hoy.</span>
                }
              </div>
            )}

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim() || !canGenerate}
              className="w-full py-4 bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono uppercase tracking-widest text-sm rounded-xl transition-all flex items-center justify-center gap-2"
            >
              {isGenerating
                ? <><Loader2 className="w-5 h-5 animate-spin" /> Generando vídeo...</>
                : <><Wand2 className="w-5 h-5" /> Generar vídeo</>
              }
            </button>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm font-mono">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                  <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* RIGHT: Preview */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex flex-col">
            <AnimatePresence mode="wait">
              {isGenerating ? (
                <motion.div key="loading"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex-1 min-h-[400px] bg-zinc-900 border border-zinc-800 rounded-2xl flex flex-col items-center justify-center gap-6">
                  <div className="relative w-20 h-20">
                    <div className="absolute inset-0 rounded-full border-2 border-red-500/20 animate-ping" />
                    <div className="absolute inset-2 rounded-full border-2 border-red-500/40 animate-ping" style={{ animationDelay: '0.3s' }} />
                    <div className="absolute inset-4 flex items-center justify-center">
                      <Clapperboard className="w-8 h-8 text-red-500 animate-pulse" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="font-mono text-red-400 text-sm">Generando tu vídeo con Seedance 2.0...</p>
                    <p className="font-mono text-zinc-600 text-xs mt-1">Esto puede tardar 1-3 minutos</p>
                  </div>
                </motion.div>
              ) : generatedVideo ? (
                <motion.div key="result"
                  initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  className="flex-1 flex flex-col gap-4">
                  <div className="relative rounded-2xl overflow-hidden border border-red-500/20 bg-zinc-900">
                    <video src={generatedVideo} controls autoPlay loop className="w-full" />
                    <div className="absolute top-3 right-3 flex gap-2">
                      <button onClick={() => setGeneratedVideo(null)} className="p-2 bg-zinc-900/80 backdrop-blur rounded-lg text-zinc-400 hover:text-white border border-zinc-700 transition-all">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      {user && (
                        <button onClick={() => setShowPublishModal(true)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600/80 backdrop-blur rounded-lg text-white border border-blue-500 transition-all hover:bg-blue-500 font-mono text-xs">
                          <Globe className="w-3.5 h-3.5" /> Publicar
                        </button>
                      )}
                      {isPro && (
                        <button onClick={handleDownload} className="p-2 bg-red-600/80 backdrop-blur rounded-lg text-white border border-red-500 transition-all hover:bg-red-500">
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="font-mono text-xs text-zinc-600 italic">"{currentPrompt}"</p>
                </motion.div>
              ) : (
                <motion.div key="empty"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex-1 min-h-[400px] bg-zinc-900 border-2 border-dashed border-zinc-800 rounded-2xl flex flex-col items-center justify-center gap-4 text-center p-8">
                  <div className="w-20 h-20 rounded-2xl bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                    <Play className="w-10 h-10 text-zinc-600" />
                  </div>
                  <div>
                    <p className="font-heading text-lg font-bold text-zinc-500">Tu vídeo aparecerá aquí</p>
                    <p className="text-zinc-600 text-sm mt-1">Describe una escena y pulsa Generar</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Pro banner */}
        {!isPro && (
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-4 justify-between">
            <div className="text-center sm:text-left">
              <p className="font-heading text-xl font-bold mb-1">Desbloquea Veritas Video Pro</p>
              <p className="text-zinc-400 text-sm">Generaciones ilimitadas con Seedance 2.0, sin límite diario y descarga directa en MP4.</p>
            </div>
            <button onClick={() => onNavigate('precios')}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-mono uppercase tracking-wider px-6 py-3 rounded-xl transition-all whitespace-nowrap flex-shrink-0">
              <Crown className="w-4 h-4" /> Ver planes
            </button>
          </motion.div>
        )}
      </main>

      <Footer onNavigate={onNavigate} currentPage="video" />

      {/* Publish Modal */}
      <AnimatePresence>
        {showPublishModal && generatedVideo && (
          <PublishModal
            mediaUrl={generatedVideo}
            mediaType="video"
            prompt={currentPrompt}
            style={style}
            aspectRatio={ratio}
            token={localStorage.getItem('veritas_token')}
            onClose={() => setShowPublishModal(false)}
            onPublished={() => {
              setShowPublishModal(false);
              onNavigate('content');
            }}
          />
        )}
      </AnimatePresence>

      {/* History Sidebar */}
      <AnimatePresence>
        {showHistory && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-40" onClick={() => setShowHistory(false)} />
            <motion.aside initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25 }} className="fixed right-0 top-0 bottom-0 w-full sm:max-w-md bg-zinc-900 border-l border-zinc-800 z-50 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-zinc-800">
                <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                  <History className="w-5 h-5 text-red-500" />
                  Historial de vídeos
                </h3>
                <div className="flex items-center gap-2">
                  {videoHistory.length > 0 && (
                    <button onClick={() => { setVideoHistory([]); localStorage.removeItem('veritas_video_history'); }} className="text-zinc-500 hover:text-red-500 transition-colors p-2">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => setShowHistory(false)} className="text-zinc-500 hover:text-white transition-colors p-2"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {videoHistory.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <Clapperboard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="font-mono text-sm">No hay vídeos generados</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {videoHistory.map((item) => (
                      <motion.div key={item.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        className="group relative flex items-start gap-3 p-3 rounded-xl border border-zinc-800 hover:border-red-500/40 transition-all bg-zinc-900">
                        <div className="w-10 h-10 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Clapperboard className="w-5 h-5 text-red-500/60" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-zinc-300 font-mono line-clamp-2 leading-relaxed">{item.prompt}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-zinc-600 font-mono">{item.ratio}</span>
                            <span className="text-zinc-700">·</span>
                            <span className="text-xs text-zinc-600">{new Date(item.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const updated = videoHistory.filter(h => h.id !== item.id);
                            setVideoHistory(updated);
                            localStorage.setItem('veritas_video_history', JSON.stringify(updated));
                          }}
                          className="p-1 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
